import { type FC, useState } from 'react'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import type {
    TransactionFilters,
    TransactionStatus,
    TransactionType,
    TransactionContext,
} from '@/types/transaction'
import { Filter } from 'lucide-react'

interface TransactionsFilterDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    currentFilters: TransactionFilters
    onApplyFilters: (filters: TransactionFilters) => void
}

export const TransactionsFilterDialog: FC<TransactionsFilterDialogProps> = ({
    open,
    onOpenChange,
    currentFilters,
    onApplyFilters,
}) => {
    const [filters, setFilters] = useState<TransactionFilters>(currentFilters)

    const handleApply = () => {
        onApplyFilters(filters)
        onOpenChange(false)
    }

    const handleReset = () => {
        const resetFilters: TransactionFilters = {
            page: 1,
            limit: 20,
        }
        setFilters(resetFilters)
        onApplyFilters(resetFilters)
        onOpenChange(false)
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                    <div className="flex items-center gap-2">
                        <div className="rounded-full bg-blue-100 p-2">
                            <Filter className="h-4 w-4 text-blue-600" />
                        </div>
                        <DialogTitle>Filter Transactions</DialogTitle>
                    </div>
                    <DialogDescription>
                        Apply filters to narrow down the transaction list
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
                    {/* Search */}
                    <div className="space-y-2">
                        <Label htmlFor="search">Search</Label>
                        <Input
                            id="search"
                            placeholder="Search by transaction ID, user ID, wallet..."
                            value={filters.search || ''}
                            onChange={e =>
                                setFilters({
                                    ...filters,
                                    search: e.target.value || undefined,
                                })
                            }
                        />
                    </div>

                    {/* Status */}
                    <div className="space-y-2">
                        <Label htmlFor="status">Status</Label>
                        <Select
                            value={(filters.status as string) || 'all'}
                            onValueChange={(value: string) =>
                                setFilters({
                                    ...filters,
                                    status:
                                        value === 'all'
                                            ? undefined
                                            : (value as TransactionStatus),
                                })
                            }
                        >
                            <SelectTrigger id="status">
                                <SelectValue placeholder="Select status" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Status</SelectItem>
                                <SelectItem value="COMPLETED">
                                    Completed
                                </SelectItem>
                                <SelectItem value="PENDING">Pending</SelectItem>
                                <SelectItem value="FAILED">Failed</SelectItem>
                                <SelectItem value="CANCELLED">
                                    Cancelled
                                </SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Transaction Type */}
                    <div className="space-y-2">
                        <Label htmlFor="type">Transaction Type</Label>
                        <Select
                            value={(filters.type as string) || 'all'}
                            onValueChange={(value: string) =>
                                setFilters({
                                    ...filters,
                                    type:
                                        value === 'all'
                                            ? undefined
                                            : (value as TransactionType),
                                })
                            }
                        >
                            <SelectTrigger id="type">
                                <SelectValue placeholder="Select type" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Types</SelectItem>
                                <SelectItem value="BUY">Buy</SelectItem>
                                <SelectItem value="SELL">Sell</SelectItem>
                                <SelectItem value="SWAP">Swap</SelectItem>
                                <SelectItem value="SEND">Send</SelectItem>
                                <SelectItem value="RECEIVE">Receive</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Transaction Context */}
                    <div className="space-y-2">
                        <Label htmlFor="context">Transaction Context</Label>
                        <Select
                            value={(filters.context as string) || 'all'}
                            onValueChange={(value: string) =>
                                setFilters({
                                    ...filters,
                                    context:
                                        value === 'all'
                                            ? undefined
                                            : (value as TransactionContext),
                                })
                            }
                        >
                            <SelectTrigger id="context">
                                <SelectValue placeholder="Select context" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">
                                    All Contexts
                                </SelectItem>
                                <SelectItem value="CRYPTO">Crypto</SelectItem>
                                <SelectItem value="FIAT">Fiat</SelectItem>
                                <SelectItem value="P2P">P2P</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Currency */}
                    <div className="space-y-2">
                        <Label htmlFor="currency">Currency/Token</Label>
                        <Select
                            value={filters.currency || 'all'}
                            onValueChange={(value: string) =>
                                setFilters({
                                    ...filters,
                                    currency:
                                        value === 'all' ? undefined : value,
                                })
                            }
                        >
                            <SelectTrigger id="currency">
                                <SelectValue placeholder="Select currency" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">
                                    All Currencies
                                </SelectItem>
                                <SelectItem value="BTC">
                                    Bitcoin (BTC)
                                </SelectItem>
                                <SelectItem value="ETH">
                                    Ethereum (ETH)
                                </SelectItem>
                                <SelectItem value="USDT">
                                    Tether (USDT)
                                </SelectItem>
                                <SelectItem value="USDC">
                                    USD Coin (USDC)
                                </SelectItem>
                                <SelectItem value="DOGE">
                                    Dogecoin (DOGE)
                                </SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    {/* User ID */}
                    <div className="space-y-2">
                        <Label htmlFor="userId">User ID</Label>
                        <Input
                            id="userId"
                            placeholder="Enter user ID..."
                            value={filters.userId || ''}
                            onChange={e =>
                                setFilters({
                                    ...filters,
                                    userId: e.target.value || undefined,
                                })
                            }
                        />
                    </div>

                    {/* Date Range */}
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="startDate">Start Date</Label>
                            <Input
                                id="startDate"
                                type="date"
                                value={filters.startDate || ''}
                                onChange={e =>
                                    setFilters({
                                        ...filters,
                                        startDate: e.target.value || undefined,
                                    })
                                }
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="endDate">End Date</Label>
                            <Input
                                id="endDate"
                                type="date"
                                value={filters.endDate || ''}
                                onChange={e =>
                                    setFilters({
                                        ...filters,
                                        endDate: e.target.value || undefined,
                                    })
                                }
                            />
                        </div>
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={handleReset}>
                        Reset Filters
                    </Button>
                    <Button onClick={handleApply}>Apply Filters</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
